import { PRE_THREEJS_CUTOVER_BACKUP_KEY } from '../production/canonicalCutover';
import type { CrazyGamesDataModule, CrazyGamesInitStatus, CrazyGamesSdkLike } from '../platform/crazyGamesSdk';
import { LocalStoragePersistence, SAVE_STORAGE_KEY, type PersistenceProvider } from './localStorage';

/** Set after the one-time localStorage copy so a later clear cannot restore a stale local save. */
export const CRAZYGAMES_DATA_MIGRATION_MARKER = 'park_v1_crazygames_data_migrated_v1';

const MIGRATION_KEYS = [SAVE_STORAGE_KEY, PRE_THREEJS_CUTOVER_BACKUP_KEY] as const;

export class CrazyGamesDataAccessError extends Error {
  constructor() {
    super('CrazyGames Data Module access failed');
    this.name = 'CrazyGamesDataAccessError';
  }
}

export class CrazyGamesDataPersistence implements PersistenceProvider {
  constructor(
    private readonly data: CrazyGamesDataModule,
    private readonly key: string,
  ) {}

  load(): string | null {
    let value: string | null;
    try {
      value = this.data.getItem(this.key);
    } catch {
      throw new CrazyGamesDataAccessError();
    }
    if (typeof value !== 'string' || value === '') {
      return null;
    }
    return value;
  }

  save(raw: string): void {
    try {
      this.data.setItem(this.key, raw);
    } catch {
      // Keep the previous Data Module value. A failed write must not delete it.
    }
  }

  clear(): void {
    try {
      this.data.removeItem(this.key);
    } catch {
      // A failed remove leaves the existing save in place.
    }
  }
}

function readDataValue(data: CrazyGamesDataModule, key: string): string | null {
  const value = data.getItem(key);
  if (typeof value !== 'string' || value === '') {
    return null;
  }
  return value;
}

function readLegacyLocalValue(key: string): string | null {
  try {
    const storage = typeof window !== 'undefined' ? window.localStorage : globalThis.localStorage;
    const value = storage?.getItem(key);
    if (typeof value !== 'string' || value === '') {
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

/**
 * Copy canonical localStorage saves into Data Module once.
 * A non-empty Data Module value always wins. The marker blocks later resurrection.
 */
export function migrateLegacyLocalSavesToDataModule(data: CrazyGamesDataModule): boolean {
  try {
    if (readDataValue(data, CRAZYGAMES_DATA_MIGRATION_MARKER)) {
      return true;
    }
    for (const key of MIGRATION_KEYS) {
      if (readDataValue(data, key)) {
        continue;
      }
      const legacy = readLegacyLocalValue(key);
      if (legacy) {
        data.setItem(key, legacy);
      }
    }
    data.setItem(CRAZYGAMES_DATA_MIGRATION_MARKER, '1');
    return true;
  } catch {
    return false;
  }
}

export type CanonicalPersistenceSelection =
  | {
      ok: true;
      backend: 'data-module';
      sdk: CrazyGamesSdkLike;
      persistence: CrazyGamesDataPersistence;
      backup: CrazyGamesDataPersistence;
    }
  | {
      ok: true;
      backend: 'local-storage';
      persistence: LocalStoragePersistence;
      backup: LocalStoragePersistence;
    }
  | { ok: false; reason: 'data_unavailable' | 'data_access_failed' };

export function selectCanonicalPersistence(
  status: CrazyGamesInitStatus,
  sdk: CrazyGamesSdkLike | null,
): CanonicalPersistenceSelection {
  if (!status.usable) {
    return {
      ok: true,
      backend: 'local-storage',
      persistence: new LocalStoragePersistence(SAVE_STORAGE_KEY),
      backup: new LocalStoragePersistence(PRE_THREEJS_CUTOVER_BACKUP_KEY),
    };
  }
  if (!sdk?.data) {
    return { ok: false, reason: 'data_unavailable' };
  }
  if (!migrateLegacyLocalSavesToDataModule(sdk.data)) {
    return { ok: false, reason: 'data_access_failed' };
  }
  return {
    ok: true,
    backend: 'data-module',
    sdk,
    persistence: new CrazyGamesDataPersistence(sdk.data, SAVE_STORAGE_KEY),
    backup: new CrazyGamesDataPersistence(sdk.data, PRE_THREEJS_CUTOVER_BACKUP_KEY),
  };
}
